// @ts-nocheck
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Calendar, Loader2, Plus, Trash2, ShoppingCart, ArrowLeft, Tag, Check, Info, CalendarClock,
} from "lucide-react";

const SESSION_KEY = "rle_schedule_requests_cart_v1";

interface InputFieldSpec {
  field_key: string;
  label: string;
  input_type: "date" | "time" | "team_select" | "team_multiselect" | "team_search_select" | "team_search_multiselect";
  required?: boolean;
  help_text?: string;
}

interface CartItem {
  id: string;
  event_id: string;
  event_name: string;
  team_id: string;
  team_name: string;
  registration_id?: string | null;
  template_id: string;
  offering_id: string;
  template_label: string;
  constraint_type: string | null;
  severity: string | null;
  fee: number;
  requires_review: boolean;
  parameters: Record<string, any>;
  notes: string;
}

const money = (n: number) => `$${(Number(n) || 0).toFixed(2)}`;

export default function ScheduleRequestsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Cart restored from session (post-login redirect)
  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  const [selectedEventId, setSelectedEventId] = useState("");
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; discount_type: string; discount_value: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<string[] | null>(null);

  // Persist cart
  useEffect(() => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(cart));
  }, [cart]);

  // Handle Stripe return URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cartSession = params.get("cart_session");
    const cartCanceled = params.get("cart_canceled");

    if (cartCanceled) {
      toast.info("Checkout canceled. Your draft requests are still pending payment — submit again or contact support.");
      window.history.replaceState({}, "", "/schedule-requests");
      return;
    }

    if (cartSession) {
      (async () => {
        try {
          const { data, error } = await (supabase as any).functions.invoke(
            "rle-finalize-constraint-checkout",
            { body: { session_id: cartSession } }
          );
          if (error) throw new Error(error.message || "Finalize failed");
          if (!data?.ok) throw new Error(data?.error || "Finalize failed");

          const ids = (data.requests ?? []).map((r: any) => r.id);
          setDone(ids);
          setCart([]);
          sessionStorage.removeItem(SESSION_KEY);
          toast.success(
            `Payment received — ${ids.length} request${ids.length === 1 ? "" : "s"} submitted for review.`
          );
        } catch (e: any) {
          toast.error(`Could not finalize checkout: ${e.message}. If you were charged, contact support.`);
        } finally {
          window.history.replaceState({}, "", "/schedule-requests");
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Eligible events: published, not deleted/archived, not too old, with at least one active offering
  const { data: events = [], isLoading: loadingEvents } = useQuery({
    queryKey: ["sr-eligible-events"],
    queryFn: async () => {
      const { data: offerings } = await (supabase as any)
        .from("event_constraint_offerings")
        .select("event_id")
        .eq("is_active", true);
      const eventIds = Array.from(new Set((offerings || []).map((o: any) => o.event_id)));
      if (eventIds.length === 0) return [];
      const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 7);
      const { data: evs } = await (supabase as any)
        .from("events")
        .select("id, name, slug, start_date, end_date, organization_id, main_venue:venues!main_venue_id(name, city, state)")
        .in("id", eventIds)
        .eq("status", "published")
        .or("is_demo.is.null,is_demo.eq.false")
        .is("deleted_at", null)
        .or("is_archived.is.null,is_archived.eq.false")
        .gte("end_date", cutoff.toISOString().split("T")[0])
        .order("start_date", { ascending: true });
      return evs || [];
    },
  });

  const selectedEvent = events.find((e: any) => e.id === selectedEventId) || null;

  // Registered teams via edge function (works signed out; RecruitLook owns names)
  const { data: teamsData, isLoading: loadingTeams } = useQuery({
    queryKey: ["sr-event-teams", selectedEventId, user?.id ?? "anon"],
    enabled: !!selectedEventId,
    queryFn: async () => {
      const { data, error } = await (supabase as any).functions.invoke(
        "rle-list-event-registered-teams",
        { body: { event_id: selectedEventId } }
      );
      if (error) { console.error(error); return { teams: [], signed_in: false }; }
      if (!data?.ok) { console.error(data?.error); return { teams: [], signed_in: false }; }
      return { teams: data.data?.teams ?? [], signed_in: !!data.data?.signed_in };
    },
  });

  const teams = teamsData?.teams ?? [];
  const teamLabel = (t: any) => t?.label || t?.team_name || "Team";

  // The signed-in coach's OWN registrations for this event. A team can be
  // registered by a club admin and coached by someone else, so check both.
  const { data: myTeamIds, isLoading: loadingMine } = useQuery({
    queryKey: ["sr-my-regs", selectedEventId, user?.id ?? "anon"],
    enabled: !!selectedEventId && !!user?.id,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("event_registrations_v2")
        .select("id, team_id, recruitlook_team_id, status")
        .eq("event_id", selectedEventId)
        .or(`coach_user_id.eq.${user.id},registered_by_user_id.eq.${user.id}`);
      if (error) { console.error(error); return new Set<string>(); }
      const out = new Set<string>();
      for (const r of data ?? []) {
        if (String(r.status || "").toLowerCase() === "withdrawn") continue;
        const id = String(r.recruitlook_team_id || r.team_id || "");
        if (id) out.add(id);
      }
      return out;
    },
  });

  // Operators (org members / event staff / platform admins) may file on behalf
  // of any team in the event — the same set the backend already authorises.
  const { data: isOperator = false } = useQuery({
    queryKey: ["sr-operator", selectedEventId, selectedEvent?.organization_id, user?.id ?? "anon"],
    enabled: !!selectedEventId && !!user?.id,
    queryFn: async () => {
      const orgId = selectedEvent?.organization_id;
      const [rolesRes, memRes, staffRes] = await Promise.all([
        (supabase as any).from("user_roles").select("role").eq("user_id", user.id),
        orgId
          ? (supabase as any)
              .from("organization_members")
              .select("id")
              .eq("user_id", user.id)
              .eq("organization_id", orgId)
              .eq("status", "active")
              .limit(1)
          : Promise.resolve({ data: [] }),
        orgId
          ? (supabase as any)
              .from("event_staff")
              .select("id")
              .eq("user_id", user.id)
              .eq("organization_id", orgId)
              .eq("is_active", true)
              .limit(1)
          : Promise.resolve({ data: [] }),
      ]);
      const roles = (rolesRes?.data ?? []).map((r: any) => r.role);
      if (roles.includes("admin") || roles.includes("rlevents_super_admin")) return true;
      if ((memRes?.data ?? []).length > 0) return true;
      if ((staffRes?.data ?? []).length > 0) return true;
      return false;
    },
  });

  // Step 2 offers the coach's own teams. Operators additionally get the full
  // event list, clearly labelled, because they act on someone else's behalf.
  const myTeams = useMemo(
    () => (myTeamIds ? teams.filter((t: any) => myTeamIds.has(String(t.team_id))) : []),
    [teams, myTeamIds]
  );

  const operatorScope = isOperator && teams.length > myTeams.length;
  const pickerTeams = operatorScope ? teams : myTeams;

  // Exactly one registration: preselect it.
  useEffect(() => {
    if (!operatorScope && myTeams.length === 1 && !selectedTeamId) setSelectedTeamId(myTeams[0].team_id);
  }, [myTeams, selectedTeamId, operatorScope]);

  const selectedTeam = teams.find((t: any) => t.team_id === selectedTeamId) || null;




  // Offerings
  const { data: offerings = [], isLoading: loadingOfferings } = useQuery({
    queryKey: ["sr-offerings", selectedEventId],
    enabled: !!selectedEventId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("event_constraint_offerings")
        .select(`
          id, override_fee, override_severity, override_requires_review, is_active, template_id, display_order,
          constraint_templates!template_id (
            id, label, description, constraint_type, default_severity, requires_review, default_fee, input_field_specs
          )
        `)
        .eq("event_id", selectedEventId)
        .eq("is_active", true)
        .order("display_order", { ascending: true });
      return data || [];
    },
  });

  // Constraint builder local state
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [draftParams, setDraftParams] = useState<Record<string, any>>({});
  useEffect(() => { setSelectedTemplateId(""); setDraftParams({}); }, [selectedTeamId, selectedEventId]);

  const teamOptions = useMemo(
    () => teams.filter((t: any) => t.team_id !== selectedTeamId).map((t: any) => ({
      value: `team:${t.team_id}`,
      label: teamLabel(t),
    })),
    [teams, selectedTeamId]
  );

  const effective = (o: any) => {
    const t = o.constraint_templates;
    return {
      offering_id: o.id,
      template_id: o.template_id,
      label: t?.label ?? "Request",
      description: t?.description ?? null,
      fee: Number(o.override_fee ?? t?.default_fee ?? 0),
      severity: o.override_severity ?? t?.default_severity ?? null,
      requires_review: o.override_requires_review ?? t?.requires_review ?? false,
      constraint_type: t?.constraint_type ?? null,
      input_field_specs: (t?.input_field_specs ?? []) as InputFieldSpec[],
    };
  };

  const selectedOffering = offerings.find((o: any) => o.template_id === selectedTemplateId);
  const selectedEff = selectedOffering ? effective(selectedOffering) : null;

  const requiredFilled = !selectedEff || selectedEff.input_field_specs.every((spec) => {
    if (spec.required === false) return true;
    const v = draftParams[spec.field_key];
    if (spec.input_type === "team_multiselect" || spec.input_type === "team_search_multiselect") return Array.isArray(v) && v.length > 0;
    return v !== undefined && v !== null && String(v).trim() !== "";
  });

  const addRequest = () => {
    if (!selectedEff || !selectedEvent || !selectedTeam) return;
    if (!requiredFilled) { toast.error("Please fill required fields"); return; }
    const item: CartItem = {
      id: crypto.randomUUID(),
      event_id: selectedEvent.id,
      event_name: selectedEvent.name,
      team_id: selectedTeam.team_id,
      team_name: teamLabel(selectedTeam),
      registration_id: selectedTeam.registration_id ?? null,
      template_id: selectedEff.template_id,
      offering_id: selectedEff.offering_id,
      template_label: selectedEff.label,
      constraint_type: selectedEff.constraint_type,
      severity: selectedEff.severity,
      fee: selectedEff.fee,
      requires_review: selectedEff.requires_review,
      parameters: { ...draftParams },
      notes: "",
    };
    setCart((c) => [...c, item]);
    setSelectedTemplateId(""); setDraftParams({}); setDraftNotes("");
    toast.success("Request added to cart");
  };

  const removeFromCart = (id: string) => setCart((c) => c.filter((i) => i.id !== id));

  // Totals & coupon
  const subtotal = cart.reduce((s, c) => s + (c.fee || 0), 0);
  const discount = appliedCoupon
    ? appliedCoupon.discount_type === "percentage"
      ? subtotal * (appliedCoupon.discount_value / 100)
      : Math.min(appliedCoupon.discount_value, subtotal)
    : 0;
  const total = Math.max(0, subtotal - discount);

  const applyCoupon = async () => {
    const code = coupon.trim();
    if (!code) return;
    const { data } = await (supabase as any)
      .from("coupons")
      .select("code, discount_type, discount_value, is_active")
      .ilike("code", code)
      .eq("is_active", true)
      .maybeSingle();
    if (!data) { toast.error("Invalid coupon"); return; }
    setAppliedCoupon({ code: data.code, discount_type: data.discount_type, discount_value: Number(data.discount_value) });
    toast.success(`Coupon ${data.code} applied`);
  };

  const checkout = async () => {
    if (cart.length === 0) return;
    if (!user) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(cart));
      navigate(`/login?next=${encodeURIComponent("/schedule-requests")}`);
      return;
    }

    setSubmitting(true);
    try {
      // Re-call the function per event now that we're signed in: registration_id
      // is only returned for authenticated callers.
      const eventIds = Array.from(new Set(cart.map((c) => c.event_id)));
      const regByEventTeam = new Map<string, string>();
      for (const eventId of eventIds) {
        const { data, error } = await (supabase as any).functions.invoke(
          "rle-list-event-registered-teams",
          { body: { event_id: eventId } }
        );
        if (error) throw new Error(error.message || "Couldn't load your registered teams");
        if (!data?.ok) throw new Error(data?.error || "Couldn't load your registered teams");
        for (const t of data.data?.teams ?? []) {
          if (t.registration_id) regByEventTeam.set(`${eventId}:${t.team_id}`, t.registration_id);
        }
      }

      const items = cart.map((c) => {
        const registration_id =
          regByEventTeam.get(`${c.event_id}:${c.team_id}`) || c.registration_id;
        if (!registration_id) {
          throw new Error(`No registration found for ${c.team_name} in ${c.event_name}`);
        }
        return {
          registration_id,
          constraint_type: c.constraint_type,
          parameters: c.parameters,
          template_id: c.template_id,
          offering_id: c.offering_id,
        };
      });


      const { data, error } = await (supabase as any).functions.invoke(
        "rle-create-constraint-checkout",
        {
          body: {
            items,
            coupon_code: appliedCoupon?.code ?? undefined,
            success_url: `${window.location.origin}/purchase/receipt?session_id={CHECKOUT_SESSION_ID}`,
          },
        }
      );
      if (error) throw new Error(error.message || "Checkout failed");
      if (!data?.ok) throw new Error(data?.error || "Checkout failed");

      if (data.requires_payment) {
        // Don't clear the cart yet — clear it on successful finalize after Stripe.
        window.location.assign(data.checkout_url);
        return;
      }

      // Free path — already inserted as pending_review.
      setDone(data.constraint_ids ?? []);
      setCart([]);
      sessionStorage.removeItem(SESSION_KEY);
      toast.success("Requests submitted!");
    } catch (e: any) {
      toast.error(e.message || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  // Receipt
  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Card>
          <CardContent className="p-8 text-center space-y-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 text-green-600">
              <Check className="w-6 h-6" />
            </div>
            <h1 className="text-2xl font-bold">Requests submitted</h1>
            <p className="text-muted-foreground">{done.length} schedule request{done.length === 1 ? "" : "s"} saved. Status: pending review.</p>
            <Button onClick={() => { setDone(null); }}>Submit more requests</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
        <div className="text-center mb-10 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <CalendarClock className="h-4 w-4" /> Schedule Requests
          </div>
          <h1 className="text-3xl md:text-5xl font-bold">Submit a Schedule Request</h1>
          <p className="text-muted-foreground mt-3 text-base md:text-lg">
            Request scheduling accommodations for your registered teams across any event.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Wizard */}
          <div className="lg:col-span-2 space-y-6">
            {/* Event */}
            <Card>
              <CardContent className="p-5 space-y-3">
                <Label className="text-base font-semibold">1. Select an Event</Label>
                {loadingEvents ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                ) : events.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No events currently accept schedule requests.</p>
                ) : (
                  <Select value={selectedEventId} onValueChange={(v) => { setSelectedEventId(v); setSelectedTeamId(""); }}>
                    <SelectTrigger className="h-12"><SelectValue placeholder="Choose an event..." /></SelectTrigger>
                    <SelectContent>
                      {events.map((e: any) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.name} — {e.start_date}
                          {e.main_venue?.city ? ` — ${e.main_venue.city}, ${e.main_venue.state}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </CardContent>
            </Card>

            {/* Team */}
            {selectedEventId && (
              <Card>
                <CardContent className="p-5 space-y-3">
                  <Label className="text-base font-semibold">2. Select Your Team</Label>
                  {!user ? (
                    <div className="space-y-2">
                      <p className="text-base text-muted-foreground">
                        Sign in to pick one of your registered teams.
                      </p>
                      <Button
                        variant="outline"
                        className="h-11"
                        onClick={() =>
                          navigate(`/login?next=${encodeURIComponent("/schedule-requests")}`)
                        }
                      >
                        Sign in
                      </Button>
                    </div>
                  ) : loadingTeams || loadingMine ? (
                    <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                  ) : pickerTeams.length === 0 ? (
                    <p className="text-base text-muted-foreground">
                      You don't have a team registered for this event.
                    </p>
                  ) : (
                    <>
                      {operatorScope && (
                        <p className="text-sm text-muted-foreground">
                          All teams in this event (operator) — you're filing on a team's behalf.
                        </p>
                      )}
                      <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
                        <SelectTrigger className="h-12 text-base">
                          <SelectValue placeholder={operatorScope ? "Choose a team..." : "Choose your team..."} />
                        </SelectTrigger>
                        <SelectContent>
                          {pickerTeams.map((t: any) => (
                            <SelectItem key={t.team_id} value={t.team_id} className="text-base min-h-[44px]">
                              {teamLabel(t)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </>
                  )}


                </CardContent>
              </Card>
            )}

            {/* Constraint builder */}
            {selectedEventId && selectedTeamId && (
              <Card>
                <CardContent className="p-5 space-y-4">
                  <Label className="text-base font-semibold">3. Build Your Request</Label>
                  {loadingOfferings ? (
                    <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                  ) : offerings.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No request types configured.</p>
                  ) : (
                    <>
                      <div className="space-y-1">
                        <Label className="text-sm">Request type</Label>
                        <Select value={selectedTemplateId} onValueChange={(v) => { setSelectedTemplateId(v); setDraftParams({}); }}>
                          <SelectTrigger><SelectValue placeholder="Select request type..." /></SelectTrigger>
                          <SelectContent>
                            {offerings.map((o: any) => {
                              const eff = effective(o);
                              return (
                                <SelectItem key={o.id} value={eff.template_id}>
                                  {eff.label} {eff.fee > 0 ? `· ${money(eff.fee)}` : "· Free"}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        {selectedEff?.description && (
                          <p className="text-xs italic text-muted-foreground mt-1">{selectedEff.description}</p>
                        )}
                      </div>

                      {selectedEff && selectedEff.input_field_specs.map((spec) => {
                        const required = spec.required !== false;
                        const val = draftParams[spec.field_key];
                        const setVal = (v: any) => setDraftParams((p) => ({ ...p, [spec.field_key]: v }));
                        const lbl = `${spec.label}${required ? " *" : ""}`;
                        if (spec.input_type === "date") return (
                          <div key={spec.field_key} className="space-y-1">
                            <Label className="text-xs">{lbl}</Label>
                            <Input type="date" min={selectedEvent?.start_date || undefined} max={selectedEvent?.end_date || undefined}
                              value={val || ""} onChange={(e) => setVal(e.target.value)} />
                            {spec.help_text && <p className="text-[11px] text-muted-foreground">{spec.help_text}</p>}
                          </div>
                        );
                        if (spec.input_type === "time") return (
                          <div key={spec.field_key} className="space-y-1">
                            <Label className="text-xs">{lbl}</Label>
                            <Input type="time" value={val || ""} onChange={(e) => setVal(e.target.value)} />
                            {spec.help_text && <p className="text-[11px] text-muted-foreground">{spec.help_text}</p>}
                          </div>
                        );
                        if (spec.input_type === "team_select") return (
                          <div key={spec.field_key} className="space-y-1">
                            <Label className="text-xs">{lbl}</Label>
                            <Select value={val || undefined} onValueChange={setVal}>
                              <SelectTrigger><SelectValue placeholder="Select team..." /></SelectTrigger>
                              <SelectContent>
                                {teamOptions.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                        if (spec.input_type === "team_multiselect") {
                          const sel: string[] = Array.isArray(val) ? val : [];
                          return (
                            <div key={spec.field_key} className="space-y-1">
                              <Label className="text-xs">{lbl}</Label>
                              <div className="space-y-1 border rounded p-2 max-h-48 overflow-auto">
                                {teamOptions.map((opt) => (
                                  <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
                                    <Checkbox checked={sel.includes(opt.value)} onCheckedChange={(c) => {
                                      if (c) setVal([...sel, opt.value]);
                                      else setVal(sel.filter((v) => v !== opt.value));
                                    }} />
                                    {opt.label}
                                  </label>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        if (spec.input_type === "team_search_select") {
                          const eligible = teams.filter((t: any) => t.team_id !== selectedTeamId);
                          return (
                            <div key={spec.field_key} className="space-y-1">
                              <Label className="text-xs">{lbl}</Label>
                              <Select value={val || undefined} onValueChange={setVal}>
                                <SelectTrigger><SelectValue placeholder="Select a team..." /></SelectTrigger>
                                <SelectContent>
                                  {eligible.map((t: any) => (
                                    <SelectItem key={t.team_id} value={t.team_id}>{teamLabel(t)}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {spec.help_text && <p className="text-[11px] text-muted-foreground">{spec.help_text}</p>}
                            </div>
                          );
                        }
                        if (spec.input_type === "team_search_multiselect") {
                          const eligible = teams.filter((t: any) => t.team_id !== selectedTeamId);
                          const sel: string[] = Array.isArray(val) ? val : [];
                          return (
                            <div key={spec.field_key} className="space-y-1">
                              <Label className="text-xs">{lbl}</Label>
                              <div className="space-y-1 border rounded p-2 max-h-48 overflow-auto">
                                {eligible.length === 0 ? (
                                  <p className="text-sm text-muted-foreground p-1">No other teams available.</p>
                                ) : eligible.map((t: any) => (
                                  <label key={t.team_id} className="flex items-center gap-2 text-sm cursor-pointer">
                                    <Checkbox checked={sel.includes(t.team_id)} onCheckedChange={(c) => {
                                      if (c) setVal([...sel, t.team_id]);
                                      else setVal(sel.filter((v) => v !== t.team_id));
                                    }} />
                                    {teamLabel(t)}
                                  </label>
                                ))}
                              </div>
                              {sel.length > 0 && <p className="text-[11px] text-muted-foreground">{sel.length} team{sel.length === 1 ? "" : "s"} selected</p>}
                              {spec.help_text && <p className="text-[11px] text-muted-foreground">{spec.help_text}</p>}
                            </div>
                          );
                        }
                        return null;
                      })}

                      {selectedEff && (
                        <>
                          <div className="flex items-center justify-between flex-wrap gap-2 pt-2">
                            <div className="text-xs text-muted-foreground">
                              <span className="font-medium text-foreground">Fee: {selectedEff.fee > 0 ? money(selectedEff.fee) : "Free"}</span>
                              {selectedEff.requires_review && <span className="ml-2">· Requires review</span>}
                            </div>
                            <Button className="h-11 text-base w-full sm:w-auto" onClick={addRequest} disabled={!requiredFilled || !selectedTeam}>
                              <Plus className="w-4 h-4 mr-1" /> Add to Cart
                            </Button>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Cart sidebar */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="p-5 space-y-2">
                <div className="flex items-start gap-2">
                  <Info className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <h3 className="font-semibold">How It Works</h3>
                </div>
                <ul className="text-xs text-muted-foreground ml-7 list-disc space-y-1">
                  <li>Select an event, team, then build each request.</li>
                  <li>Add multiple requests across teams and events.</li>
                  <li>Pay once at checkout. Coupon codes supported.</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <ShoppingCart className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold">Your Cart</h3>
                  <span className="ml-auto bg-primary/10 text-primary text-xs font-bold px-2 py-0.5 rounded-full">{cart.length}</span>
                </div>

                {cart.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No requests yet.</p>
                ) : (
                  <div className="space-y-3">
                    {cart.map((item) => (
                      <div key={item.id} className="flex items-start justify-between gap-2 py-2 border-b last:border-0">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{item.template_label}</p>
                          <p className="text-xs text-muted-foreground truncate">{item.team_name} · {item.event_name}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-sm font-semibold">{item.fee > 0 ? money(item.fee) : "Free"}</span>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeFromCart(item.id)}>
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}

                    <div className="pt-2 border-t space-y-2">
                      <div className="flex items-center gap-2">
                        <Tag className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Coupon</span>
                      </div>
                      {appliedCoupon ? (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-green-600 font-medium">✓ {appliedCoupon.code} applied</span>
                          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setAppliedCoupon(null); setCoupon(""); }}>Remove</Button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Input className="h-9 text-sm" placeholder="Code" value={coupon} onChange={(e) => setCoupon(e.target.value)} />
                          <Button size="sm" variant="outline" className="h-9 shrink-0" onClick={applyCoupon} disabled={!coupon.trim()}>Apply</Button>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-sm font-semibold">Total</span>
                      <div className="text-right">
                        {discount > 0 && <span className="text-xs text-muted-foreground line-through mr-2">{money(subtotal)}</span>}
                        <span className="text-lg font-bold text-primary">{money(total)}</span>
                      </div>
                    </div>

                    <Button className="w-full h-11 text-base" onClick={checkout} disabled={submitting}>
                      {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : (
                        total === 0 ? "Submit Requests (Free)" : `Checkout · ${money(total)}`
                      )}
                    </Button>
                    {!user && (
                      <p className="text-[11px] text-muted-foreground text-center">You'll be asked to sign in at checkout — your cart is saved.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
